import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  DollarSign,
  AlertTriangle,
} from 'lucide-react';

/**
 * AdminRefundModal - Modal for admin-initiated refunds
 *
 * Props:
 * - isOpen: boolean - Whether modal is open
 * - onClose: function - Called when modal is closed
 * - invoice: object - Invoice to refund
 * - onRefundCreated: function - Called when refund is created
 */
const AdminRefundModal = ({
  isOpen,
  onClose,
  invoice,
  onRefundCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);

  // Form state
  const [refundType, setRefundType] = useState('full');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('customer_request');
  const [reasonNotes, setReasonNotes] = useState('');
  const [skipApproval, setSkipApproval] = useState(false);

  const reasonLabels = {
    duplicate_charge: 'Duplicate Charge',
    service_not_provided: 'Service Not Provided',
    billing_error: 'Billing Error',
    customer_request: 'Customer Request',
    subscription_cancellation: 'Subscription Cancellation',
    other: 'Other',
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && invoice) {
      setRefundType('full');
      setAmount((invoice.total / 100).toFixed(2));
      setReason('customer_request');
      setReasonNotes('');
      setSkipApproval(false);
      setError('');
      setSuccess(false);
      setResult(null);
    }
  }, [isOpen, invoice]);

  // Update amount when refund type changes
  useEffect(() => {
    if (invoice && refundType === 'full') {
      setAmount((invoice.total / 100).toFixed(2));
    }
  }, [refundType, invoice]);

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const maxRefundable = invoice ? invoice.total / 100 : 0;
  const amountCents = Math.round(parseFloat(amount || 0) * 100);
  const requiresDualApproval = amountCents >= 10000; // $100+

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > maxRefundable) {
      setError(`Amount cannot exceed ${formatCurrency(maxRefundable)}`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post(
        `${getApiBase()}/admin/billing/refunds/initiate`,
        {
          invoice_id: invoice.id,
          amount: amountCents,
          reason: reason,
          reason_notes: reasonNotes.trim() || null,
          skip_approval: skipApproval,
        },
        { headers: getAuthHeaders() }
      );

      setResult(response.data);
      setSuccess(true);

      // Notify parent after delay
      setTimeout(() => {
        if (onRefundCreated) {
          onRefundCreated();
        }
        onClose();
      }, 3000);
    } catch (err) {
      console.error('Error creating refund:', err);
      setError(err.response?.data?.detail || 'Failed to create refund');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !invoice) return null;

  // Success state
  if (success && result) {
    const creditNoteSuccess = result.credit_note_status === 'created';
    const creditNoteFailed = result.credit_note_status === 'failed';
    const creditNoteSkipped = result.credit_note_status === 'skipped';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {result.status === 'completed' || result.status === 'succeeded'
              ? 'Refund Processed'
              : 'Refund Initiated'}
          </h3>
          <p className="text-gray-600">
            {formatCurrency(amount)} refund for invoice {invoice.invoice_number}
            {result.requires_approval
              ? ' is pending approval.'
              : ' has been processed.'}
          </p>
          {result.requires_dual_approval && (
            <p className="text-sm text-amber-600 mt-2">
              This refund requires dual approval due to the amount.
            </p>
          )}

          {/* Credit Note Status */}
          {creditNoteSuccess && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                Credit note created in Xero successfully.
              </p>
            </div>
          )}
          {creditNoteFailed && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start justify-center">
                <AlertTriangle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm text-red-700 font-medium">
                    Credit note creation failed
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    {result.credit_note_error || 'Unknown error'}
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    You may need to create a credit note manually in Xero.
                  </p>
                </div>
              </div>
            </div>
          )}
          {creditNoteSkipped && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start justify-center">
                <AlertTriangle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm text-amber-700">
                    Credit note not created: {result.credit_note_error || 'Xero integration not configured'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Issue Refund</h3>
            <p className="text-sm text-gray-500">
              Invoice {invoice.invoice_number}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-800">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Invoice Summary */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Organization:</span>
              <p className="font-medium text-gray-900">
                {invoice.organization?.name || 'Unknown'}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Invoice Total:</span>
              <p className="font-medium text-gray-900">
                {formatCurrency(invoice.total / 100)}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Refund Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Refund Amount
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="refundType"
                  value="full"
                  checked={refundType === 'full'}
                  onChange={(e) => setRefundType(e.target.value)}
                  className="mr-2"
                />
                <span>Full refund ({formatCurrency(maxRefundable)})</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="refundType"
                  value="partial"
                  checked={refundType === 'partial'}
                  onChange={(e) => setRefundType(e.target.value)}
                  className="mr-2"
                />
                <span>Partial refund</span>
              </label>
            </div>
          </div>

          {/* Amount (for partial) */}
          {refundType === 'partial' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (USD)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={maxRefundable}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Maximum: {formatCurrency(maxRefundable)}
              </p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(reasonLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={reasonNotes}
              onChange={(e) => setReasonNotes(e.target.value)}
              rows={2}
              placeholder="Additional details about the refund..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Skip Approval Option */}
          <div className="border-t pt-4">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={skipApproval}
                onChange={(e) => setSkipApproval(e.target.checked)}
                className="mt-1 mr-3"
              />
              <div>
                <span className="font-medium text-gray-900">
                  Process immediately (skip approval)
                </span>
                <p className="text-sm text-gray-500">
                  Refund will be processed through Stripe immediately without
                  waiting for approval workflow.
                </p>
              </div>
            </label>
          </div>

          {/* Warning for dual approval */}
          {requiresDualApproval && !skipApproval && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertTriangle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  Refunds of $100 or more require dual approval. This refund
                  will need approval from two authorized users before processing.
                </p>
              </div>
            </div>
          )}

          {/* Warning for skip approval */}
          {skipApproval && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertTriangle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">
                  <strong>Warning:</strong> This will immediately refund{' '}
                  {formatCurrency(amount)} to the customer's payment method.
                  This action cannot be undone.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 rounded-md flex items-center gap-2 ${
                skipApproval
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              } disabled:opacity-50`}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {skipApproval ? 'Process Refund Now' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminRefundModal;
